<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-btn
          class="mr-2 mb-2"
          color="primary"
          elevation="2"
          :disabled="!formModel.id || Number(formModel.status) < 4"
          @click="handleProformaDialog"
        >
          Proforma
        </v-btn>
        <v-btn
          class="mr-2 mb-2"
          color="primary"
          elevation="2"
          :disabled="!formModel.id || Number(formModel.status) < 4"
          @click="handleDepositInvoiceDialog"
        >
          Deposit Invoice
        </v-btn>
        <v-btn
          class="mr-2 mb-2"
          color="primary"
          elevation="2"
          :disabled="!formModel.id || !canCreateInvoice || Number(formModel.status) < 4"
          @click="handleInvoiceDialog"
        >
          Invoice
        </v-btn>
        <v-btn
          class="mr-2 mb-2"
          color="primary"
          elevation="2"
          :disabled="!formModel.id || !canCreateFinalInvoice || Number(formModel.status) < 4"
          @click="openFinalInvoiceDialog"
        >
          Final Invoice
        </v-btn>
        <v-btn
          class="mr-2 mb-2"
          color="primary"
          elevation="2"
          :disabled="!formModel.id"
          @click="openReceiptDialog"
        >
          Receipt
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="formModel.id" class="mb-1">
      <v-col cols="12" sm="6" lg="3">
        <v-card outlined class="summary-card fill-height">
          <v-card-text>
            <div class="text-title-large mb-1">Total documents</div>
            <div class="text-h6 font-weight-bold">{{ sortedDocuments.length }}</div>
            <div class="caption mt-2">Paid: {{ paidDocumentsCount }} · Pending: {{ pendingDocumentsCount }} · Storno: {{ reversedDocumentsCount }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card outlined class="summary-card fill-height">
          <v-card-text>
            <div class="text-caption text--secondary mb-1">Order gross total</div>
            <div class="text-h6 font-weight-bold">{{ formatDocumentAmount(orderGrossAmount) }}</div>
            <div class="caption mt-2">Calculated from the sales order total</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card outlined class="summary-card summary-card--success fill-height">
          <v-card-text>
            <div class="text-caption text--secondary mb-1">Paid amount</div>
            <div class="text-h6 font-weight-bold success--text">{{ formatDocumentAmount(totalPaidGrossAmount) }}</div>
            <div class="caption mt-2">
              <span v-if="totalPaidGrossAmount > 0">Received payments total</span>
              <span v-else>No payment received yet</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card outlined class="summary-card fill-height" :class="remainingGrossAmount > 0 ? 'summary-card--warning' : 'summary-card--neutral'">
          <v-card-text>
            <div class="text-caption text--secondary mb-1">Remaining amount</div>
            <div class="text-h6 font-weight-bold" :class="remainingGrossAmount > 0 ? 'warning--text text--darken-2' : 'success--text'">
              {{ formatDocumentAmount(remainingGrossAmount) }}
            </div>
            <div class="caption mt-2">
              <span v-if="remainingGrossAmount > 0">Outstanding balance</span>
              <span v-else>Fully paid</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <div class="so-view-switcher">
          <v-btn
            small
            :color="documentTypeFilter === 'all' ? 'primary' : undefined"
            :outlined="documentTypeFilter !== 'all'"
            @click="documentTypeFilter = 'all'"
          >
            All
          </v-btn>
          <v-btn
            small
            :color="documentTypeFilter === 'proforma' ? 'primary' : undefined"
            :outlined="documentTypeFilter !== 'proforma'"
            @click="documentTypeFilter = 'proforma'"
          >
            Proforma
          </v-btn>
          <v-btn
            small
            :color="documentTypeFilter === 'deposit_invoice' ? 'primary' : undefined"
            :outlined="documentTypeFilter !== 'deposit_invoice'"
            @click="documentTypeFilter = 'deposit_invoice'"
          >
            Deposit Invoice
          </v-btn>
          <v-btn
            small
            :color="documentTypeFilter === 'invoice' ? 'primary' : undefined"
            :outlined="documentTypeFilter !== 'invoice'"
            @click="documentTypeFilter = 'invoice'"
          >
            Invoice
          </v-btn>
          <v-btn
            small
            :color="documentTypeFilter === 'final_invoice' ? 'primary' : undefined"
            :outlined="documentTypeFilter !== 'final_invoice'"
            @click="documentTypeFilter = 'final_invoice'"
          >
            Final Invoice
          </v-btn>
          <v-btn
            small
            :color="documentTypeFilter === 'reverse_invoice' ? 'primary' : undefined"
            :outlined="documentTypeFilter !== 'reverse_invoice'"
            @click="documentTypeFilter = 'reverse_invoice'"
          >
            Storno invoices
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="!formModel.id">
      <v-col cols="12">
        <v-alert outlined dense type="info">
          Save the sales order first, then you can create and review the related documents here.
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12">
        <v-simple-table class="document-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th>Type</th>
                <th>Document no.</th>
                <th>Status</th>
                <th>Issued</th>
                <th>Payment deadline</th>
                <th>Payment method</th>
                <th class="text-right">Net</th>
                <th class="text-right">Gross</th>
                <th>Paid</th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="displayDocuments.length === 0">
                <td colspan="10" class="text-center py-4">
                  No documents found for the selected filter.
                </td>
              </tr>
              <tr
                v-for="documentRow in displayDocuments"
                :key="documentRow.rowKey"
                :class="documentRowClass(documentRow)"
              >
                <td>
                  <div
                    class="document-type-cell d-flex align-center"
                    :class="documentRowIndentClass(documentRow)"
                  >
                    <div class="document-icon-wrap" :class="documentRowIconClass(documentRow)">
                      <v-icon small :color="documentRowIconColor(documentRow)">
                        {{ documentRowIcon(documentRow) }}
                      </v-icon>
                    </div>
                    <div class="min-width-0">
                      <div class="d-flex align-center flex-wrap">
                        <span :class="documentRowTitleClass(documentRow)">
                          {{ documentTypeLabel(documentRow.document) }}
                        </span>
                        <v-chip
                          v-if="documentRow.relationType === 'reverse'"
                          x-small
                          label
                          color="error"
                          dark
                          class="ml-2"
                        >
                          Storno
                        </v-chip>
                        <v-chip
                          v-else-if="isDocumentReversed(documentRow.document)"
                          x-small
                          label
                          outlined
                          color="error"
                          class="ml-2"
                        >
                          Reversed
                        </v-chip>
                      </div>
                      <div
                        v-if="documentRelationCaption(documentRow)"
                        class="caption text--secondary document-relation-caption"
                      >
                        {{ documentRelationCaption(documentRow) }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-center justify-space-between">
                    <span>{{ documentRow.document.document_number || '-' }}</span>
                    <v-chip
                      v-if="documentRow.relationType === 'reverse'"
                      x-small
                      outlined
                      color="error"
                      class="ml-2"
                    >
                      Linked storno
                    </v-chip>
                    <v-chip
                      v-else-if="documentRow.relationType === 'proforma_child'"
                      x-small
                      outlined
                      color="primary"
                      class="ml-2"
                    >
                      Linked
                    </v-chip>
                    <v-chip
                      v-else-if="documentRow.reverseChildrenCount > 0"
                      x-small
                      outlined
                      color="error"
                      class="ml-2"
                    >
                      Has storno
                    </v-chip>
                    <v-chip
                      v-else-if="documentRow.linkedChildrenCount > 0"
                      x-small
                      outlined
                      color="primary"
                      class="ml-2"
                    >
                      Parent
                    </v-chip>
                  </div>
                </td>
                <td>
                  <v-chip
                    x-small
                    label
                    :color="documentStatusColor(documentRow.document.status)"
                    dark
                  >
                    {{ documentStatusLabel(documentRow.document.status) }}
                  </v-chip>
                </td>
                <td>{{ formatDocumentDate(documentRow.document.issued_at || documentRow.document.created_at) }}</td>
                <td>{{ formatDocumentDate(documentRow.document.payment_deadline || documentRow.document.due_at) }}</td>
                <td>{{ documentRow.document.payment_method || '-' }}</td>
                <td class="text-right" :class="documentAmountClass(documentRow.document)">{{ formatSignedDocumentAmount(documentRow.document, "net_amount") }}</td>
                <td class="text-right" :class="documentAmountClass(documentRow.document)">{{ formatSignedDocumentAmount(documentRow.document, "gross_amount") }}</td>
                <td>{{ documentPaidLabel(documentRow.document) }}</td>
                <td class="text-right">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="canDeleteProforma(documentRow.document)"
                        small
                        outlined
                        color="error"
                        :disabled="invoiceLoading"
                        class="mr-1"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="deleteProforma(documentRow.document)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete proforma</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="canReverseDocument(documentRow.document)"
                        small
                        outlined
                        color="warning"
                        class="mr-1"
                        :disabled="invoiceLoading || loadingReverse"
                        :loading="
                          loadingReverse &&
                            selectedReverseDocument &&
                            selectedReverseDocument.id === documentRow.document.id
                        "
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="openReverseInvoiceDialog(documentRow.document)"
                      >
                        <v-icon small>mdi-undo</v-icon>
                      </v-btn>
                    </template>
                    <span>Storno invoice</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        class="pdf-btn"
                        :disabled="!documentRow.document.pdf_path"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="openDocumentPdf(documentRow.document)"
                      >
                        <v-icon small>mdi-file-pdf</v-icon>
                      </v-btn>
                    </template>
                    <span>Open PDF</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <ProformaDialog
      v-model="proformaDialog"
      :loading="invoiceLoading"
      :order-id="formModel.id"
      :initial-data="proformaData"
      :invoice-datas="formModel"
      :seller="invoiceSeller"
      @save="issueProforma"
    />
    <DepositDialog
      v-model="depositDialog"
      :loading="invoiceLoading"
      :order-id="formModel.id"
      :initial-data="depositData"
      :invoice-datas="formModel"
      :seller="invoiceSeller"
      @save="issueDeposit"
    />
    <InvoiceDialog
      v-model="invoiceDialog"
      :loading="invoiceLoading"
      :order-id="formModel.id"
      :initial-data="invoiceData"
      :invoice-datas="formModel"
      :seller="invoiceSeller"
      @submit="issueInvoice"
    />
    <FinalInvoiceDialog
      v-model="finalInvoiceDialog"
      :loading="invoiceLoading"
      :order-id="formModel.id"
      :initial-data="finalInvoiceFormPr"
      :invoice-datas="formModel"
      :seller="invoiceSeller"
      @save="issueFinalInvoice"
    />
    <ReceiptDialog
      v-model="showReceiptDialog"
      :loading="invoiceLoading"
      :order-id="formModel.id"
      :invoice-datas="formModel"
      :seller="invoiceSeller"
      @save="issueReceipt"
    />
    <ReverseInvoiceDialog
      v-model="reverseInvoiceDialog"
      :loading="loadingReverse"
      :document="selectedReverseDocument"
      :seller="invoiceSeller"
      :currency="
        selectedPriceList && selectedPriceList.currency_id
          ? selectedPriceList.currency_id
          : 'HUF'
      "
      @save="submitReverseInvoice"
    />
    <BaseSnackbar
      v-model="uSnackbar.show"
      :text="uSnackbar.text"
      :color="uSnackbar.color"
      :timeout="uSnackbar.timeout"
      :bottom="false"
      :top="true"
      :right="false"
    />
    <acceptance-receipt-dialog
      v-model="acceptanceReceiptDialog"
      :sales-order-id="formModel.id"
      @generated="reloadDocuments"
    />
  </div>
</template>

<script>
import { ENDPOINT } from "../SalesOrders";
import ApiService from "@/core/services/api.service";
import ProformaDialog from "./ProformaDialog.vue";
import DepositDialog from "./DepositDialog.vue";
import InvoiceDialog from "./InvoiceDialog.vue";
import FinalInvoiceDialog from "./FinalInvoiceDialog.vue";
import ReceiptDialog from "./ReceiptDialog.vue";
import ReverseInvoiceDialog from "./ReverseInvoiceDialog.vue";
import BaseSnackbar from "./BaseSnackbar.vue";
import AcceptanceReceiptDialog from "./AcceptanceReceiptDialog.vue";
import Swal from "sweetalert2";

const createProformaData = () => ({
  id: null,
  type: "Előleg",
  mode: "percent",
  percent: null,
  value: null,
  vat: 27,
  fulfillmentDate: null,
});

const createDepositData = () => ({
  id: null,
  type: "Előleg",
  mode: "percent",
  percent: null,
  value: null,
  vat: 27,
  fulfillmentDate: null,
});

const createInvoiceData = () => ({
  priceList: null,
  tax: 27,
  zero_tax: null,
  items: [],
});

const createFinalInvoicePayload = () => ({
  fulfillmentDate: null,
  paymentDeadline: null,
  note: null,
});

const createFinalInvoiceDialogData = () => ({
  fulfillmentDate: null,
  paymentDeadline: null,
  note: null,
  paymentMethod: "Átutalás",
  proformaNumber: null,
});

export default {
  name: "SalesOrderInvoicesPanel",
  components: {
    ProformaDialog,
    DepositDialog,
    InvoiceDialog,
    FinalInvoiceDialog,
    ReceiptDialog,
    ReverseInvoiceDialog,
    BaseSnackbar,
    AcceptanceReceiptDialog,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    selectedPriceList: {
      type: Object,
      default: () => ({ currency_id: "HUF" }),
    },
  },
  data() {
    return {
      documentTypeFilter: "all",
      proformaDialog: false,
      proformaData: createProformaData(),
      depositDialog: false,
      depositData: createDepositData(),
      invoiceDialog: false,
      invoiceData: createInvoiceData(),
      invoiceLoading: false,
      finalInvoiceDialog: false,
      finalInvoiceForm: createFinalInvoicePayload(),
      finalInvoiceFormPr: createFinalInvoiceDialogData(),
      showReceiptDialog: false,
      invoiceSeller: {
        name: "Kismetál Kft.",
        address: "5051 Zagyvarékas, Fekete Lajos út 82.",
        taxNumber: "13023946-2-16, HU13023946",
        email: "info@kismetal.hu",
        phone: "06202673818",
        bankAccount: "695001561102397100000000",
      },
      uSnackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
      acceptanceReceiptDialog: false,
      selectedSalesOrderId: null,
      reverseInvoiceDialog: false,
      selectedReverseDocument: null,
      loadingReverse: false,
    };
  },
  computed: {
    normalizedDocuments() {
      if (!Array.isArray(this.formModel.documents)) {
        return [];
      }

      return this.formModel.documents.map((document) => ({
        ...document,
        type: this.normalizeDocumentType(document.type),
      }));
    },

    sortedDocuments() {
      return [...this.normalizedDocuments].sort((a, b) => {
        const aDate = new Date(a.issued_at || a.created_at || 0).getTime();
        const bDate = new Date(b.issued_at || b.created_at || 0).getTime();

        return bDate - aDate;
      });
    },

    filteredDocuments() {
      if (this.documentTypeFilter === "all") {
        return this.sortedDocuments;
      }

      return this.sortedDocuments.filter(
        (document) => document.type === this.documentTypeFilter
      );
    },

    displayDocuments() {
      if (this.documentTypeFilter !== "all") {
        return this.filteredDocuments.map((document) => {
          const originalDocument = this.findOriginalDocumentForReverse(document);
          const reverseChildren = this.getReverseChildrenForDocument(document);

          return this.createDocumentRow(
            document,
            null,
            0,
            this.normalizeDocumentType(document.type) === "reverse_invoice" ? "reverse" : null,
            0,
            reverseChildren.length,
            originalDocument
          );
        });
      }

      const proformaDocuments = this.sortedDocuments.filter(
        (document) => document.type === "proforma"
      );
      const proformaIdSet = new Set(
        proformaDocuments.map((document) => this.normalizeComparableId(document.id))
      );

      const reverseDocumentsByOriginalId = this.sortedDocuments
        .filter((document) => {
          return (
            document.type === "reverse_invoice" &&
            this.normalizeComparableId(document.original_document_id)
          );
        })
        .reduce((groups, document) => {
          const parentId = this.normalizeComparableId(document.original_document_id);

          if (!groups[parentId]) {
            groups[parentId] = [];
          }

          groups[parentId].push(document);
          return groups;
        }, {});

      Object.keys(reverseDocumentsByOriginalId).forEach((parentId) => {
        reverseDocumentsByOriginalId[parentId] = this.sortDocumentsByDate(
          reverseDocumentsByOriginalId[parentId]
        );
      });

      const linkedDocumentsByProformaId = this.sortedDocuments
        .filter((document) => {
          const type = this.normalizeDocumentType(document.type);
          const proformaId = this.normalizeComparableId(document.proforma_id);

          return (
            ["deposit_invoice", "invoice"].includes(type) &&
            proformaIdSet.has(proformaId)
          );
        })
        .reduce((groups, document) => {
          const parentId = this.normalizeComparableId(document.proforma_id);

          if (!groups[parentId]) {
            groups[parentId] = [];
          }

          groups[parentId].push(document);
          return groups;
        }, {});

      Object.keys(linkedDocumentsByProformaId).forEach((parentId) => {
        linkedDocumentsByProformaId[parentId] = this.sortDocumentsByDate(
          linkedDocumentsByProformaId[parentId]
        );
      });

      const groupedChildIds = new Set(
        Object.values(linkedDocumentsByProformaId)
          .flat()
          .map((document) => this.normalizeComparableId(document.id))
      );

      const groupedReverseIds = new Set(
        Object.values(reverseDocumentsByOriginalId)
          .flat()
          .map((document) => this.normalizeComparableId(document.id))
      );

      const rows = [];

      const pushReverseChildren = (parentDocument, level) => {
        const parentId = this.normalizeComparableId(parentDocument.id);
        const reverseChildren = reverseDocumentsByOriginalId[parentId] || [];

        reverseChildren.forEach((reverseDocument) => {
          rows.push(
            this.createDocumentRow(
              reverseDocument,
              parentDocument,
              level + 1,
              "reverse",
              0,
              0,
              parentDocument
            )
          );
        });
      };

      this.sortedDocuments.forEach((document) => {
        const documentId = this.normalizeComparableId(document.id);
        const type = this.normalizeDocumentType(document.type);

        if (type === "reverse_invoice" && groupedReverseIds.has(documentId)) {
          return;
        }

        if (type === "proforma") {
          const linkedChildren =
            linkedDocumentsByProformaId[this.normalizeComparableId(document.id)] || [];
          const reverseChildren = reverseDocumentsByOriginalId[documentId] || [];

          rows.push(
            this.createDocumentRow(
              document,
              null,
              0,
              null,
              linkedChildren.length,
              reverseChildren.length
            )
          );

          linkedChildren.forEach((childDocument) => {
            const childReverseChildren =
              reverseDocumentsByOriginalId[this.normalizeComparableId(childDocument.id)] || [];

            rows.push(
              this.createDocumentRow(
                childDocument,
                document,
                1,
                "proforma_child",
                0,
                childReverseChildren.length
              )
            );

            pushReverseChildren(childDocument, 1);
          });

          pushReverseChildren(document, 0);

          return;
        }

        if (
          ["deposit_invoice", "invoice"].includes(type) &&
          groupedChildIds.has(documentId)
        ) {
          return;
        }

        const reverseChildren = reverseDocumentsByOriginalId[documentId] || [];

        rows.push(
          this.createDocumentRow(
            document,
            null,
            0,
            null,
            0,
            reverseChildren.length
          )
        );

        pushReverseChildren(document, 0);
      });

      return rows;
    },

    canCreateInvoice() {
      return !this.sortedDocuments.some((document) => {
        return (
          this.normalizeDocumentType(document.type) === "invoice" &&
          !this.isDocumentReversed(document)
        );
      });
    },

    canCreateFinalInvoice() {
      return !this.sortedDocuments.some((document) => {
        return (
          this.normalizeDocumentType(document.type) === "final_invoice" &&
          !this.isDocumentReversed(document)
        );
      });
    },

    activeDocuments() {
      return this.sortedDocuments.filter((document) => {
        return (
          this.normalizeDocumentType(document.type) !== "reverse_invoice" &&
          !this.isDocumentReversed(document)
        );
      });
    },

    paidDocumentsCount() {
      return this.activeDocuments.filter((document) => this.isDocumentPaid(document)).length;
    },

    pendingDocumentsCount() {
      return this.activeDocuments.filter((document) => !this.isDocumentPaid(document)).length;
    },

    reversedDocumentsCount() {
      return this.sortedDocuments.filter((document) => {
        return (
          this.normalizeDocumentType(document.type) === "reverse_invoice" ||
          this.isDocumentReversed(document)
        );
      }).length;
    },

    orderGrossAmount() {
      const candidates = [
        this.formModel.total_gross,
        this.formModel.price_gross,
        this.formModel.gross_amount,
        this.formModel.grossAmount,
      ];

      const directValue = candidates.find((value) => {
        const numericValue = Number(value);
        return !Number.isNaN(numericValue) && numericValue > 0;
      });

      if (directValue !== undefined) {
        return Number(directValue);
      }

      const invoiceLikeDocument = this.activeDocuments.find((document) =>
        ["final_invoice", "invoice", "deposit_invoice", "proforma"].includes(document.type)
      );

      const fallbackValue = invoiceLikeDocument
        ? Number(invoiceLikeDocument.gross_amount)
        : 0;

      return Number.isNaN(fallbackValue) ? 0 : fallbackValue;
    },

    totalPaidGrossAmount() {
      return this.activeDocuments.reduce((sum, document) => {
        const type = this.normalizeDocumentType(document.type);

        if (!["deposit_invoice", "invoice", "final_invoice"].includes(type)) {
          return sum;
        }

        return sum + this.getDocumentPaidGrossAmount(document);
      }, 0);
    },

    remainingGrossAmount() {
      const remaining = this.orderGrossAmount - this.totalPaidGrossAmount;
      return remaining > 0 ? remaining : 0;
    },
  },
  methods: {
    ensureDocumentsArray() {
      if (!Array.isArray(this.formModel.documents)) {
        this.$set(this.formModel, "documents", []);
      }
    },

    normalizeDocumentType(type) {
      if (!type) return "";

      const normalized = String(type).toLowerCase().replace(/-/g, "_");

      if (normalized === "preinvoice") return "proforma";
      if (normalized === "pre_invoice") return "proforma";
      if (normalized === "advance_invoice") return "invoice";

      return normalized;
    },

    normalizeComparableId(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      return String(value);
    },

    getDocumentTimestamp(document) {
      return new Date(document.issued_at || document.created_at || 0).getTime();
    },

    sortDocumentsByDate(documents) {
      return [...documents].sort((a, b) => {
        return this.getDocumentTimestamp(b) - this.getDocumentTimestamp(a);
      });
    },

    createDocumentRow(
      document,
      parentDocument = null,
      level = 0,
      relationType = null,
      linkedChildrenCount = 0,
      reverseChildrenCount = 0,
      originalDocument = null
    ) {
      return {
        rowKey: this.buildDocumentRowKey(document, parentDocument, relationType),
        document,
        isParent: linkedChildrenCount > 0 || reverseChildrenCount > 0,
        isChild: level > 0,
        parentDocument,
        originalDocument,
        childrenCount: linkedChildrenCount + reverseChildrenCount,
        linkedChildrenCount,
        reverseChildrenCount,
        relationType,
        level,
      };
    },

    buildDocumentRowKey(document, parentDocument = null, relationType = null) {
      const documentId = document && document.id ? document.id : "new";
      const parentId = parentDocument && parentDocument.id ? parentDocument.id : "root";

      if (relationType) {
        return `document-${documentId}-${relationType}-of-${parentId}`;
      }

      return `document-${documentId}`;
    },

    getReverseChildrenForDocument(document) {
      if (!document || !document.id) {
        return [];
      }

      const documentId = this.normalizeComparableId(document.id);

      return this.sortedDocuments.filter((candidate) => {
        return (
          this.normalizeDocumentType(candidate.type) === "reverse_invoice" &&
          this.normalizeComparableId(candidate.original_document_id) === documentId
        );
      });
    },

    findOriginalDocumentForReverse(document) {
      if (
        !document ||
        this.normalizeDocumentType(document.type) !== "reverse_invoice" ||
        !document.original_document_id
      ) {
        return null;
      }

      const originalDocumentId = this.normalizeComparableId(document.original_document_id);

      return this.sortedDocuments.find((candidate) => {
        return this.normalizeComparableId(candidate.id) === originalDocumentId;
      }) || null;
    },

    isReverseInvoice(document) {
      return this.normalizeDocumentType(document && document.type) === "reverse_invoice";
    },

    isDocumentReversed(document) {
      if (!document) {
        return false;
      }

      const status = String(document.status || "").toLowerCase();
      const reverseStatus = String(document.reverse_status || "").toLowerCase();

      return (
        status === "reversed" ||
        reverseStatus === "reversed" ||
        Boolean(document.reversed_by_document_id)
      );
    },

    documentRowIndentClass(documentRow) {
      const level = documentRow && documentRow.level ? documentRow.level : 0;

      return `document-cell--level-${level}`;
    },

    documentRowIcon(documentRow) {
      if (documentRow && documentRow.relationType === "reverse") {
        return "mdi-file-undo";
      }

      if (documentRow && documentRow.relationType === "proforma_child") {
        return "mdi-subdirectory-arrow-right";
      }

      const type = this.normalizeDocumentType(documentRow && documentRow.document
        ? documentRow.document.type
        : null
      );

      if (type === "proforma") return "mdi-file-document";
      if (type === "deposit_invoice") return "mdi-file-percent";
      if (type === "final_invoice") return "mdi-file-check";
      if (type === "receipt") return "mdi-receipt";
      if (type === "reverse_invoice") return "mdi-file-rotate-left";

      return "mdi-file-document";
    },

    documentRowIconColor(documentRow) {
      if (documentRow && documentRow.relationType === "reverse") {
        return "error";
      }

      if (documentRow && documentRow.relationType === "proforma_child") {
        return "primary";
      }

      if (documentRow && this.isDocumentReversed(documentRow.document)) {
        return "error";
      }

      const type = this.normalizeDocumentType(documentRow && documentRow.document
        ? documentRow.document.type
        : null
      );

      if (type === "proforma") return "indigo";
      if (type === "deposit_invoice") return "deep-purple";
      if (type === "final_invoice") return "success";
      if (type === "receipt") return "teal";
      if (type === "reverse_invoice") return "error";

      return "primary";
    },

    documentRowIconClass(documentRow) {
      return {
        "document-icon-wrap--reverse": documentRow && documentRow.relationType === "reverse",
        "document-icon-wrap--reversed": documentRow && this.isDocumentReversed(documentRow.document),
      };
    },

    documentRowTitleClass(documentRow) {
      return {
        "font-weight-medium": documentRow && (documentRow.isParent || !documentRow.isChild),
        "error--text": documentRow && (
          documentRow.relationType === "reverse" ||
          this.isDocumentReversed(documentRow.document)
        ),
      };
    },

    documentRelationCaption(documentRow) {
      if (!documentRow || !documentRow.document) {
        return "";
      }

      if (documentRow.relationType === "reverse") {
        const parentNumber = documentRow.parentDocument &&
          documentRow.parentDocument.document_number
          ? documentRow.parentDocument.document_number
          : "the original invoice";

        return `Storno invoice linked to ${parentNumber}`;
      }

      if (documentRow.relationType === "proforma_child") {
        const parentNumber = documentRow.parentDocument &&
          documentRow.parentDocument.document_number
          ? documentRow.parentDocument.document_number
          : "proforma";

        return `Linked to ${parentNumber}`;
      }

      if (documentRow.reverseChildrenCount > 0) {
        const reverseChildren = this.getReverseChildrenForDocument(documentRow.document);
        const reverseNumbers = reverseChildren
          .map((document) => document.document_number)
          .filter(Boolean)
          .join(", ");

        return reverseNumbers
          ? `Storno invoice: ${reverseNumbers}`
          : "Has linked storno invoice";
      }

      if (documentRow.linkedChildrenCount > 0) {
        return `${documentRow.linkedChildrenCount} linked ${
          documentRow.linkedChildrenCount === 1 ? "document" : "documents"
        }`;
      }

      if (this.isReverseInvoice(documentRow.document)) {
        const originalDocument = documentRow.originalDocument ||
          this.findOriginalDocumentForReverse(documentRow.document);

        if (originalDocument && originalDocument.document_number) {
          return `Storno invoice linked to ${originalDocument.document_number}`;
        }
      }

      return "";
    },

    documentAmountClass(document) {
      return {
        "error--text": this.isReverseInvoice(document),
        "text--secondary": this.isDocumentReversed(document) && !this.isReverseInvoice(document),
      };
    },

    getSignedDocumentAmount(document, field) {
      const numericValue = Number(document && document[field] ? document[field] : 0);

      if (Number.isNaN(numericValue)) {
        return 0;
      }

      if (this.isReverseInvoice(document)) {
        return -1 * Math.abs(numericValue);
      }

      return numericValue;
    },

    formatSignedDocumentAmount(document, field) {
      return this.formatDocumentAmount(this.getSignedDocumentAmount(document, field));
    },

    documentRowClass(documentRow) {
      const classes = {
        "document-row--parent": documentRow.isParent,
        "document-row--child": documentRow.isChild,
        "document-row--reverse": documentRow.relationType === "reverse",
        "document-row--reversed": this.isDocumentReversed(documentRow.document),
      };

      classes[`document-row--level-${documentRow.level || 0}`] = true;

      return classes;
    },

    documentTypeLabel(document) {
      const type = this.normalizeDocumentType(document.type);

      if (type === "proforma") return "Proforma";
      if (type === "deposit_invoice") return "Deposit Invoice";
      if (type === "invoice") return "Invoice";
      if (type === "final_invoice") return "Final Invoice";
      if (type === "reverse_invoice") return "Storno Invoice";
      if (type === "receipt") return "Receipt";

      return document.type || "-";
    },

    documentStatusLabel(status) {
      if (!status) return "-";

      const normalized = String(status).toLowerCase();

      if (normalized === "pending") return "Pending";
      if (normalized === "paid") return "Paid";
      if (normalized === "partial") return "Partially paid";
      if (normalized === "cancelled") return "Cancelled";
      if (normalized === "fulfilled") return "Fulfilled";
      if (normalized === "issued") return "Issued";
      if (normalized === "reversed") return "Reversed";

      return normalized.replace(/_/g, " ");
    },

    documentStatusColor(status) {
      const normalized = String(status || "").toLowerCase();

      if (normalized === "paid" || normalized === "fulfilled") return "success";
      if (normalized === "partial") return "warning";
      if (normalized === "cancelled") return "error";
      if (normalized === "issued") return "info";
      if (normalized === "reversed") return "error";

      return "primary";
    },

    formatDocumentDate(value) {
      if (!value) return "-";

      const normalized = String(value).replace(" ", "T");
      const date = new Date(normalized);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString("hu-HU");
    },

    formatDocumentAmount(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      const currency =
        (this.selectedPriceList && this.selectedPriceList.currency_id) || "HUF";
      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return value;
      }

      return this.$helpers.getNicePrice(numericValue, currency);
    },

    documentPaidLabel(document) {
      if (document.paid_at) {
        return this.formatDocumentDate(document.paid_at);
      }

      const paidAmount = Number(document && document.paid_gross_amount ? document.paid_gross_amount : 0);
      if (!Number.isNaN(paidAmount) && paidAmount > 0) {
        return `${this.formatDocumentAmount(paidAmount)} paid`;
      }

      const status = String(document && document.status ? document.status : "").toLowerCase();

      if (status === "paid") {
        return "Paid";
      }

      if (status === "partial") {
        return "Partially paid";
      }

      return "No";
    },

    getDocumentPaidGrossAmount(document) {
      if (!document) {
        return 0;
      }

      const paidGrossAmount = Number(document.paid_gross_amount || 0);

      if (!Number.isNaN(paidGrossAmount) && paidGrossAmount > 0) {
        return paidGrossAmount;
      }

      const status = String(document.status || "").toLowerCase();

      if (document.paid_at || status === "paid") {
        const grossAmount = Number(document.gross_amount || 0);
        return Number.isNaN(grossAmount) ? 0 : grossAmount;
      }

      return 0;
    },

    isDocumentPaid(document) {
      const status = String(document && document.status ? document.status : "").toLowerCase();
      const paidAmount = Number(document && document.paid_gross_amount ? document.paid_gross_amount : 0);

      return Boolean(
        document && document.paid_at ||
        (!Number.isNaN(paidAmount) && paidAmount > 0) ||
        status === "paid" ||
        status === "partial"
      );
    },

    getRelatedDocumentsForProforma(proformaDocument) {
      if (!proformaDocument || this.normalizeDocumentType(proformaDocument.type) !== "proforma") {
        return [];
      }

      const proformaId = this.normalizeComparableId(proformaDocument.id);

      return this.sortedDocuments.filter((document) => {
        if (this.normalizeComparableId(document.id) === proformaId) {
          return false;
        }

        return this.normalizeComparableId(document.proforma_id) === proformaId;
      });
    },

    canDeleteProforma(document) {
      if (!document || this.normalizeDocumentType(document.type) !== "proforma") {
        return false;
      }

      return !this.isDocumentPaid(document) && this.getRelatedDocumentsForProforma(document).length === 0;
    },

    removeDocumentLocally(documentId) {
      this.ensureDocumentsArray();

      const normalizedId = this.normalizeComparableId(documentId);
      const nextDocuments = this.formModel.documents.filter(
        (document) => this.normalizeComparableId(document.id) !== normalizedId
      );

      this.$set(this.formModel, "documents", nextDocuments);
    },

    async deleteProforma(document) {
      if (!document) return;
        
      if (!this.canDeleteProforma(document)) {
        this.showSnackbar(
          "Only unpaid and unlinked proforma documents can be deleted.",
          "warning",
          5000
        );
        return;
      }
  
      const result = await Swal.fire({
        title: "Delete proforma?",
        text: `Are you sure you want to delete proforma ${document.document_number || ""}?`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
      });
  
      const confirmed =
        result &&
        (result.value === true || result.isConfirmed === true);
  
      if (!confirmed) {
        return;
      }
  
      this.invoiceLoading = true;
  
      try {
        await ApiService.delete(ENDPOINT + "deleteProforma/" + document.id);
    
        this.removeDocumentLocally(document.id);
    
        await Swal.fire({
          title: "Deleted",
          text: "Proforma deleted successfully.",
          type: "success",
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (error) {
        this.showRequestError(
          error,
          "Error occurred while deleting proforma invoice"
        );
      } finally {
        this.invoiceLoading = false;
      }
    },

    getDocumentPdfUrl(pdfPath) {
      if (!pdfPath) return null;
      if (/^https?:\/\//i.test(pdfPath)) return pdfPath;

      const baseUrl = (
        process.env.VUE_APP_BACKEND_URL ||
        process.env.VUE_APP_PORTAL_URL ||
        process.env.VUE_APP_PORTAL ||
        window.location.origin ||
        ""
      ).replace(/\/$/, "");
      const normalizedPath = String(pdfPath).replace(/^\/+/, "");

      return `${baseUrl}/public/${normalizedPath}`;
    },

    openDocumentPdf(document) {
      const pdfUrl = this.getDocumentPdfUrl(document.pdf_path);

      if (!pdfUrl) return;

      window.open(pdfUrl, "_blank");
    },

    appendDocument(document) {
      this.ensureDocumentsArray();

      const normalizedDocument = {
        ...document,
        type: this.normalizeDocumentType(document.type),
      };

      const existingIndex = this.formModel.documents.findIndex((item) => {
        return this.normalizeComparableId(item.id) ===
          this.normalizeComparableId(normalizedDocument.id);
      });

      if (existingIndex === -1) {
        this.formModel.documents.unshift(normalizedDocument);
      } else {
        this.$set(this.formModel.documents, existingIndex, normalizedDocument);
      }
    },

    handleProformaDialog() {
      this.proformaData = {
        ...this.proformaData,
        id: this.formModel.id,
      };
      this.proformaDialog = true;
    },

    handleDepositInvoiceDialog() {
      this.depositData = {
        ...this.depositData,
        id: this.formModel.id,
      };
      this.depositDialog = true;
    },

    handleInvoiceDialog() {
      this.invoiceData = createInvoiceData();
      this.invoiceDialog = true;

      if (
        this.formModel.salesPoint &&
        this.formModel.salesPoint.name &&
        this.formModel.salesPoint.name.includes("kismetalvasker")
      ) {
        this.invoiceData.priceList = this.formModel.priceList;
        this.invoiceData.zero_tax = this.formModel.zero_tax === 1;
        this.invoiceData.items = this.formModel.items || [];
      }
    },

    openFinalInvoiceDialog() {
      this.finalInvoiceDialog = true;
    },

    openReceiptDialog() {
      this.showReceiptDialog = true;
    },

    async issueFinalInvoice(payload) {
      if (!this.formModel.id) return;
      this.invoiceLoading = true;

      this.finalInvoiceForm = {
        fulfillmentDate: payload.fulfillmentDate,
        paymentDeadline: payload.paymentDeadline,
        note: payload.note,
      };

      try {
        const { data } = await ApiService.post(
          ENDPOINT + "issueFinalInvoice/" + this.formModel.id,
          this.finalInvoiceForm
        );

        this.finalInvoiceDialog = false;
        this.appendDocument(data.document);
        this.showSnackbar("Final invoice issued successfully", "success", 5000);
      } catch (error) {
        this.showRequestError(error, "Error occurred while issuing Final invoice");
      } finally {
        this.invoiceLoading = false;
      }
    },

    async issueInvoice(payload) {
      if (!this.formModel.id) return;
      this.invoiceLoading = true;

      try {
        const { data } = await ApiService.post(
          ENDPOINT + "issueInvoice/" + this.formModel.id,
          payload
        );

        this.invoiceDialog = false;
        this.appendDocument(data.document);
        this.showSnackbar("Invoice issued successfully", "success", 5000);
      } catch (error) {
        this.showRequestError(error, "Error occurred while issuing invoice");
      } finally {
        this.invoiceLoading = false;
      }
    },

    async issueProforma(payload) {
      this.proformaData = payload;
      this.invoiceLoading = true;

      if (!this.proformaData.id) {
        this.invoiceLoading = false;
        return;
      }

      try {
        const { data } = await ApiService.post(
          ENDPOINT + "issueProforma/",
          this.proformaData
        );

        this.proformaDialog = false;
        this.appendDocument(data.document);
        this.showSnackbar(
          "Proforma invoice issued successfully",
          "success",
          5000
        );
      } catch (error) {
        this.showRequestError(error, "Error occurred while issuing proforma invoice");
      } finally {
        this.invoiceLoading = false;
      }
    },

    async issueReceipt(payload) {
      this.invoiceLoading = true;

      try {
        const { data } = await ApiService.post(ENDPOINT + "issueReceipt", payload);
        this.appendDocument(data.document);
        this.showSnackbar("Receipt issued successfully", "success", 5000);
        this.showReceiptDialog = false;
      } catch (error) {
        this.showRequestError(error, "Error occurred while issuing receipt");
      } finally {
        this.invoiceLoading = false;
      }
    },

    async issueDeposit(payload) {
      this.depositData = payload;
      this.invoiceLoading = true;

      if (!this.depositData.id) {
        this.invoiceLoading = false;
        return;
      }

      try {
        const { data } = await ApiService.post(
          ENDPOINT + "issueDeposit/",
          this.depositData
        );

        this.depositDialog = false;
        this.appendDocument(data.document);
        this.showSnackbar(
          "Deposit invoice issued successfully",
          "success",
          5000
        );
      } catch (error) {
        this.showRequestError(error, "Error occurred while issuing deposit invoice");
      } finally {
        this.invoiceLoading = false;
      }
    },

    showRequestError(error, fallbackMessage) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
          ? error.response.data.message
          : fallbackMessage;

      this.showSnackbar(message, "error", 8000);
      console.log("Document action error:", error);
      console.log("Document action response:", error && error.response);
    },

    showSnackbar(text, color = "success", timeout = 3000) {
      this.uSnackbar.text = text;
      this.uSnackbar.color = color;
      this.uSnackbar.timeout = timeout;
      this.uSnackbar.show = true;
    },

    openAcceptanceReceiptDialog(order) {
      this.selectedSalesOrderId = order.id;
      this.acceptanceReceiptDialog = true;
    },

    reloadDocuments() {
      this.getData();
    },
    openReverseInvoiceDialog(document) {
      if (!document) {
        return;
      }

      this.selectedReverseDocument = {
        ...document,
        type: this.normalizeDocumentType(document.type),
      };
      this.reverseInvoiceDialog = true;
    },

    async submitReverseInvoice(payload) {
      if (!payload || !payload.documentId) {
        return;
      }

      this.loadingReverse = true;

      try {
        const { data } = await ApiService.post(
          ENDPOINT + `${payload.documentId}/reverse`,
          {
            reason: payload.reason,
            issueDate: payload.issueDate,
            fulfillmentDate: payload.fulfillmentDate,
            isElectronicInvoice: payload.isElectronicInvoice,
            invoiceTemplate: payload.invoiceTemplate,
            buyerEmail: payload.buyerEmail,
            buyerTaxNumber: payload.buyerTaxNumber,
            buyerEuTaxNumber: payload.buyerEuTaxNumber,
            emailReplyTo: payload.emailReplyTo,
            emailSubject: payload.emailSubject,
            emailText: payload.emailText,
          }
        );

        this.reverseInvoiceDialog = false;

        if (data.document) {
          this.appendDocument(data.document);
          this.markDocumentAsReversed(payload.documentId, data.document);
        }

        this.selectedReverseDocument = null;
        this.showSnackbar("Invoice reversed successfully", "success", 5000);
      } catch (error) {
        this.showRequestError(error, "Error occurred while reversing invoice");
      } finally {
        this.loadingReverse = false;
      }
    },

    reverseDocument(document) {
      this.openReverseInvoiceDialog(document);
    },

    markDocumentAsReversed(originalDocumentId, reverseDocument) {
      this.ensureDocumentsArray();

      const normalizedId = this.normalizeComparableId(originalDocumentId);
      const existingIndex = this.formModel.documents.findIndex((document) => {
        return this.normalizeComparableId(document.id) === normalizedId;
      });

      if (existingIndex === -1) {
        return;
      }

      const currentDocument = this.formModel.documents[existingIndex];

      this.$set(this.formModel.documents, existingIndex, {
        ...currentDocument,
        status: "reversed",
        reverse_status: "reversed",
        reversed_by_document_id: reverseDocument ? reverseDocument.id : null,
      });
    },

    canReverseDocument(document) {
      if (!document) {
        return false;
      }

      const type = this.normalizeDocumentType(document.type);
      const status = String(document.status || "").toLowerCase();
      const reverseStatus = String(document.reverse_status || "").toLowerCase();

      return [
        "invoice",
        "deposit_invoice",
        "final_invoice",
      ].includes(type)
        && status !== "reversed"
        && reverseStatus !== "reversed"
        && !document.reversed_by_document_id;
    },
  },
};
</script>

<style scoped>
.so-view-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-card {
  border-radius: 12px;
}

.summary-card .text-title-large,
.summary-card .text-caption,
.summary-card .caption {
  font-size: 15px !important;
  line-height: 1.4;
}

.summary-card .text-h6 {
  font-size: 1.5rem !important;
  line-height: 1.35;
}

.summary-card--success {
  border-color: rgba(76, 175, 80, 0.35) !important;
  background: rgba(76, 175, 80, 0.04);
}

.summary-card--warning {
  border-color: rgba(255, 152, 0, 0.35) !important;
  background: rgba(255, 152, 0, 0.05);
}

.summary-card--neutral {
  border-color: rgba(76, 175, 80, 0.35) !important;
  background: rgba(76, 175, 80, 0.04);
}

.document-table th,
.document-table td {
  white-space: nowrap;
  font-size: 14px;
}

.document-table th {
  font-weight: 600;
}

.document-table td {
  vertical-align: middle;
}

.document-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
}

.document-table th {
  background: rgba(0, 0, 0, 0.035);
}

.document-table tbody tr {
  transition: background-color 0.15s ease;
}

.document-table tbody tr:hover {
  background: rgba(25, 118, 210, 0.055);
}

.document-row--parent {
  background: rgba(25, 118, 210, 0.035);
}

.document-row--child {
  background: rgba(0, 0, 0, 0.015);
}

.document-row--reverse {
  background: rgba(244, 67, 54, 0.055);
}

.document-row--reversed:not(.document-row--reverse) {
  background: rgba(244, 67, 54, 0.025);
}

.document-type-cell {
  min-width: 230px;
}

.document-cell--level-1 {
  padding-left: 22px;
  position: relative;
}

.document-cell--level-2 {
  padding-left: 44px;
  position: relative;
}

.document-cell--level-1::before,
.document-cell--level-2::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 5px;
  bottom: 5px;
  width: 2px;
  border-radius: 999px;
  background: rgba(25, 118, 210, 0.22);
}

.document-cell--level-2::before {
  left: 28px;
  background: rgba(244, 67, 54, 0.22);
}

.document-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  width: 28px;
  height: 28px;
  margin-right: 8px;
  border-radius: 999px;
  background: rgba(25, 118, 210, 0.08);
}

.document-icon-wrap--reverse,
.document-icon-wrap--reversed {
  background: rgba(244, 67, 54, 0.08);
}

.document-relation-caption {
  line-height: 1.25;
  white-space: normal;
}

.min-width-0 {
  min-width: 0;
}
</style>
